import ChunkLoading from 'src/components/views/ChunkLoading.vue'
import ChunkLoadingTimeout from 'src/components/views/ChunkLoadingTimeout.vue'
import ChunkNotFound from 'src/components/views/ChunkNotFound.vue'
import { trackIssueCustom } from 'src/boot/sentry'

const withPrefix = (prefix, routes) =>
  routes.map((route) => {
    route.path = prefix + route.path
    return route
  })

// Reference https://github.com/bencodezen/vue-enterprise-boilerplate/blob/main/src/router/routes.js
const lazyLoadView = (AsyncView) => {
  const AsyncHandler = () => ({
    component: AsyncView.then(
      o => o,
      e => {
        console.log('Page loading ERROR:', e.message)
        if (e.message) {
          if (['Loading chunk', 'Loading CSS chunk'].some(v => e.message.includes(v))) {
            return ChunkNotFound
          }
        }

        trackIssueCustom({
          section: 'Chunks loading',
          title: 'Chunks loading: unrecognized CATCH',
          data: e
        })

        throw e
      }
    ),
    // A component to use while the component is loading.
    loading: ChunkLoading,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: ChunkLoadingTimeout,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 30000
  })

  return Promise.resolve({
    functional: true,
    render (h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    }
  })
}

const routes = [
  {
    path: '/',
    component: require('layouts/BaseLayout.vue').default,
    children: [
      {
        name: 'index',
        path: '',
        component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/Filter.vue'))
      },
      {
        name: 'filter',
        path: 'filter',
        component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/Filter.vue'))
      },
      {
        name: 'restaurant',
        path: 'restaurants/:id',
        component: () => lazyLoadView(import('pages/restaurants/Item.vue'))
      },
      {
        name: 'site-cover',
        path: 'restaurants/:sid',
        alias: 'r/:sid',
        component: {
          render: c => c('router-view')
        },
        props: true,
        children: [
          {
            name: 'reserve',
            path: 'reserve',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/restaurants/createReservation/Index.vue')),
            props: true
          },
          {
            name: 'check-info',
            path: 'check-info',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/restaurants/createReservation/CheckInfo.vue')),
            props: true
          },
          {
            name: 'code-input',
            path: 'code-input',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/restaurants/createReservation/CodeInput.vue')),
            props: true
          },
          {
            name: 'pre-reserve-process',
            path: 'reserve/pre-process',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/restaurants/createReservation/PreProcess.vue')),
            props: true
          },
          {
            name: 'reserve-process',
            path: 'reserve/process',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-flow" */ 'pages/restaurants/createReservation/Process.vue')),
            props: true
          }
        ]
      },
      ...withPrefix('reservations/', [
        {
          name: 'reservations-retrieve',
          path: 'retrieve',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "retrieve-flow" */ 'pages/reservations/Retrieve.vue'))
        },
        {
          name: 'reservations-code-input',
          path: 'code-input',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "retrieve-flow" */ 'pages/reservations/CodeInput.vue')),
          props: true
        },
        {
          name: 'reservations-list',
          path: 'list',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "retrieve-flow" */ 'pages/reservations/List.vue'))
        },
        {
          name: 'reservation-edit',
          path: ':conf/edit',
          alias: '/:conf/u',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-edit-flow" */ 'pages/reservations/updateReservation/Index.vue')),
          props: true
        },
        {
          name: 'reservation-edit-check',
          path: ':conf/edit/check',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-edit-flow" */ 'pages/reservations/updateReservation/CheckInfo.vue')),
          props: true
        },
        {
          name: 'reservation-edit-process',
          path: ':conf/edit/process',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-edit-flow" */ 'pages/reservations/updateReservation/Process.vue')),
          props: true
        },
        {
          name: 'reservation-cancel',
          path: ':conf/cancel',
          alias: '/:conf/d',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-edit-flow" */ 'pages/reservations/cancelReservation/Index.vue')),
          props: true
        },
        {
          name: 'reservation-cancel-success',
          path: ':conf/cancel/success',
          alias: '/:conf/d/success',
          component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "reservation-edit-flow" */ 'pages/reservations/cancelReservation/Success.vue')),
          props: true,
          beforeEnter: (to, from, next) => {
            if (from.name && to.params.scriptInitiated) {
              next()
            } else {
              next(from.name ? false : { path: '/' })
            }
          }
        }
      ]),
      {
        name: 'user-cover',
        path: 'user/:uid',
        alias: 'u/:uid',
        component: {
          render: c => c('router-view')
        },
        props: true,
        children: [
          {
            name: 'user-edit',
            path: 'edit',
            alias: 'u',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "user-edit-flow" */ 'pages/users/updateUser/Index.vue')),
            props: true
          },
          {
            name: 'user-edit-code-input',
            path: 'edit/code-input',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "user-edit-flow" */ 'pages/users/updateUser/CodeInput.vue')),
            props: true
          },
          {
            name: 'user-edit-process',
            path: 'edit/process',
            component: () => lazyLoadView(import(/* webpackPrefetch: true, webpackChunkName: "user-edit-flow" */ 'pages/users/updateUser/Process.vue')),
            props: true
          }
        ]
      }
    ]
  }
]

// Always leave this as last one
routes.push({
  path: '*',
  component: require('pages/Error404.vue').default
})

export default routes
